import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import {
  Plus,
  Loader2,
  Edit,
  Trash2,
  Clock,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const COLORS = [
  '#8B5CF6', '#F472B6', '#2DD4BF', '#60A5FA', '#FACC15', 
  '#F87171', '#4ADE80', '#A78BFA', '#FB923C', '#94A3B8'
];

const SLOT_DURATION = 15; // Fixed 15-min slots

const DAY_KEYS = ['mon','tue','wed','thu','fri','sat','sun'];
const DAY_LABELS = { mon:'Lunes', tue:'Martes', wed:'Miércoles', thu:'Jueves', fri:'Viernes', sat:'Sábado', sun:'Domingo' };

const toMins = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const fromMins = (mins) => {
  const h = Math.floor(((mins % 1440) + 1440) / 60 % 24);
  const m = ((mins % 60) + 60) % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
};
const expandRange = (from, to, intervalMins) => {
  const times = [];
  let cur = toMins(from);
  const end = toMins(to);
  while (cur < end) { times.push(fromMins(cur)); cur += intervalMins; }
  return times;
};
const timesToRanges = (times, intervalMins) => {
  if (!times || times.length === 0) return [];
  const sorted = times.filter(t => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)).sort();
  if (sorted.length === 0) return [];
  const ranges = [];
  let start = sorted[0];
  let prev = toMins(sorted[0]);
  for (let i = 1; i < sorted.length; i++) {
    const cur = toMins(sorted[i]);
    if (cur - prev !== intervalMins) {
      ranges.push({ from: start, to: fromMins(prev + intervalMins) });
      start = sorted[i];
    }
    prev = cur;
  }
  ranges.push({ from: start, to: fromMins(prev + intervalMins) });
  return ranges;
};
const emptyRanges = () => Object.fromEntries(DAY_KEYS.map(d => [d, []]));

export const Services = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();

  const [services, setServices] = useState([]);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rangesByDay, setRangesByDay] = useState(emptyRanges());
  const [formData, setFormData] = useState({
    name: '',
    color: COLORS[0],
    capacityPerSlot: 2,
    slotsPerBooking: 2,
    active: true,
    whatsappRemindersEnabled: true,
    emailRemindersEnabled: false,
    isPublic: false,
    publicAvailability: [],
    enablePlanningVariants: false,
    variants: [],
  });

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const [calRes, centersRes] = await Promise.all([
        api.get('/calendars'),
        api.get('/centers'),
      ]);
      setServices(calRes.data);
      setCenter((centersRes.data || [])[0] || null);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error(t('services.toast.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // WhatsApp reminders are only usable when WhatsApp is active AND credits remain
  const whatsappActive = center?.whatsappStatus === 'active';
  const creditsExhausted = center?.whatsappReminderCredits !== null &&
    center?.whatsappReminderCredits !== undefined &&
    center?.whatsappReminderCredits <= 0;
  const whatsappRemindersAvailable = whatsappActive && !creditsExhausted;

  const whatsappDisabledReason = !whatsappActive
    ? 'WhatsApp no está activo en este centro'
    : creditsExhausted
      ? 'Sin créditos de mensajes WhatsApp — contacta con el administrador'
      : null;

  const handleOpenDialog = (service = null) => {
    if (service) {
      const intervalMins = (service.slotsPerBooking || 2) * SLOT_DURATION;
      setEditingService(service);
      setFormData({
        name: service.name,
        color: service.color,
        capacityPerSlot: service.capacityPerSlot,
        slotsPerBooking: service.slotsPerBooking || 2,
        active: service.active,
        whatsappRemindersEnabled: service.whatsappRemindersEnabled !== false,
        emailRemindersEnabled: service.emailRemindersEnabled === true,
        isPublic: service.isPublic ?? false,
        publicAvailability: service.publicAvailability || [],
        enablePlanningVariants: service.enablePlanningVariants === true,
        variants: Array.isArray(service.variants) ? service.variants : [],
      });
      const ranges = emptyRanges();
      (service.publicAvailability || []).forEach(({ day, times }) => {
        if (ranges[day] !== undefined) ranges[day] = timesToRanges(times, intervalMins);
      });
      setRangesByDay(ranges);
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        capacityPerSlot: 2,
        slotsPerBooking: 2,
        active: true,
        whatsappRemindersEnabled: true,
        emailRemindersEnabled: false,
        isPublic: false,
        publicAvailability: [],
        enablePlanningVariants: false,
        variants: [],
      });
      setRangesByDay(emptyRanges());
    }
    setShowDialog(true);
  };

  // R1/R8: variant CRUD in form state. id auto-UUID in FE; rename preserves id;
  // hard-delete is structurally prohibited (R8) — only soft-deactivate.
  const uuidv4 = () => ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4)).toString(16));

  const handleAddVariant = () => {
    const newVariant = {
      id: uuidv4(),
      name: `Variante ${(formData.variants || []).length + 1}`,
      color: null,
      active: true,
    };
    setFormData(f => ({ ...f, variants: [...(f.variants || []), newVariant] }));
  };

  const handleUpdateVariant = (vid, patch) => {
    setFormData(f => ({
      ...f,
      variants: (f.variants || []).map(v => v.id === vid ? { ...v, ...patch } : v),
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error(t('services.toast.nameRequired'));
      return;
    }

    const intervalMins = formData.slotsPerBooking * SLOT_DURATION;
    const pubAvail = [];
    DAY_KEYS.forEach(day => {
      const times = [];
      (rangesByDay[day] || []).forEach(({ from, to }) => {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(from) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(to)) return;
        if (toMins(from) >= toMins(to)) return;
        expandRange(from, to, intervalMins).forEach(t => times.push(t));
      });
      if (times.length > 0) pubAvail.push({ day, times });
    });
    const payload = { ...formData, publicAvailability: pubAvail };

    setFormLoading(true);
    try {
      if (editingService) {
        await api.put(`/calendars/${editingService.id}`, payload);
        toast.success(t('services.updated') || 'Service updated');
      } else {
        await api.post('/calendars', {
          ...payload,
          centerId: user.centerId || '',
        });
        toast.success(t('services.created') || 'Service created');
      }
      setShowDialog(false);
      fetchServices();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || t('services.toast.saveError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(t('services.toast.confirmDelete', { name: service.name }))) return;

    try {
      await api.delete(`/calendars/${service.id}`);
      toast.success(t('services.toast.deleted'));
      fetchServices();
    } catch (error) {
      toast.error(t('services.toast.deleteError'));
    }
  };

  // Helper to convert slots to display duration
  const slotsToDuration = (slots) => slots * SLOT_DURATION;

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="services-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          {t('services.title')}
        </h1>
        
        <Button
          className="btn-primary"
          onClick={() => handleOpenDialog()}
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('services.addService')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {t('services.noServices')}
        </div>
      ) : (
        <div className="grid gap-3">
          {services.map((service) => (
            <div
              key={service.id}
              className={`flex items-center gap-4 p-4 bg-card border rounded-sm transition-colors ${
                service.active ? 'border-zinc-800' : 'border-zinc-900 opacity-50'
              }`}
              data-testid={`service-row-${service.id}`}
            >
              <div
                className="w-4 h-12 rounded-sm flex-shrink-0"
                style={{ backgroundColor: service.color }}
              />
              
              <div className="flex-1 min-w-0">
                <div className="font-medium">{service.name}</div>
                <div className="text-sm text-zinc-500 flex items-center gap-3">
                  <span>{t('planner.capacity')}: {service.capacityPerSlot}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {slotsToDuration(service.slotsPerBooking || 2)}min ({service.slotsPerBooking || 2} {t('services.slots') || 'slots'})
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(service)}
                  data-testid={`edit-service-${service.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => handleDelete(service)}
                  data-testid={`delete-service-${service.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {editingService ? t('common.edit') : t('services.addService')}
            </DialogTitle>
	            <DialogDescription className="sr-only">
	              {t('services.manageServices')}
	            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('services.name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                className="input-tactical"
                data-testid="service-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('services.color')}</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setFormData(f => ({ ...f, color }))}
                    className={`w-8 h-8 rounded-sm transition-all ${
                      formData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-background' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('services.capacity')}</Label>
                <Input
                  type="number"
                  value={formData.capacityPerSlot}
                  onChange={(e) => setFormData(f => ({ ...f, capacityPerSlot: Number(e.target.value) }))}
                  className="input-tactical"
                  min={1}
                  data-testid="service-capacity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('services.slotsPerBooking') || 'Slots per Booking'}</Label>
                <Select
                  value={String(formData.slotsPerBooking)}
                  onValueChange={(value) => setFormData(f => ({ ...f, slotsPerBooking: Number(value) }))}
                >
                  <SelectTrigger data-testid="service-slots-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 6, 8].map((slots) => (
                      <SelectItem key={slots} value={String(slots)}>
                        {slots} {t('services.slots') || 'slots'} = {slots * SLOT_DURATION}min
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <Label>{t('common.active')}</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, active: checked }))}
                data-testid="service-active-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <div className="space-y-0.5">
                <Label>Recordatorios WhatsApp</Label>
                {whatsappDisabledReason && (
                  <p className="text-[11px] text-zinc-500">{whatsappDisabledReason}</p>
                )}
              </div>
              <Switch
                checked={formData.whatsappRemindersEnabled && whatsappRemindersAvailable}
                onCheckedChange={(checked) => {
                  if (!whatsappRemindersAvailable) return;
                  setFormData(f => ({ ...f, whatsappRemindersEnabled: checked }));
                }}
                disabled={!whatsappRemindersAvailable}
                data-testid="service-whatsapp-reminders-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <Label>Recordatorios Email</Label>
              <Switch
                checked={formData.emailRemindersEnabled}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, emailRemindersEnabled: checked }))}
                data-testid="service-email-reminders-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <Label>Visible en reservas online</Label>
              <Switch
                checked={formData.isPublic}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, isPublic: checked }))}
                data-testid="service-ispublic-switch"
              />
            </div>

            {/* R1: enablePlanningVariants toggle (D5) */}
            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <div className="space-y-0.5">
                <Label>Planificación por variantes</Label>
                <p className="text-[11px] text-zinc-500">Permite dividir slots en variantes paralelas (R4).</p>
              </div>
              <Switch
                checked={formData.enablePlanningVariants}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, enablePlanningVariants: checked }))}
                data-testid="service-enable-variants-switch"
              />
            </div>

            {/* R1+R8: Variants editor (visible whenever flag is ON; D5 diverges from 9dcd51bc) */}
            {formData.enablePlanningVariants && (
              <div className="space-y-2 border border-zinc-800 rounded-sm p-3" data-testid="variants-editor">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide text-zinc-400">Variantes</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleAddVariant}
                    data-testid="add-variant-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Añadir variante
                  </Button>
                </div>
                {(formData.variants || []).length === 0 && (
                  <p className="text-[11px] text-zinc-500">Sin variantes todavía. Crea una para empezar.</p>
                )}
                <div className="space-y-2">
                  {(formData.variants || []).map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center gap-2 p-2 bg-zinc-900 rounded-sm"
                      data-testid={`variant-row-${v.id}`}
                    >
                      <div
                        className={`w-3 h-8 rounded-sm flex-shrink-0 ${v.color ? '' : 'border border-dashed border-zinc-500'}`}
                        style={{ backgroundColor: v.color || formData.color }}
                        title={v.color ? 'Color propio' : 'Heredado del servicio'}
                        aria-label={v.color ? 'Color propio' : 'Color heredado del servicio'}
                        role="img"
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-zinc-500 font-mono" data-testid={`variant-id-${v.id}`}>
                            {v.id.slice(0, 8)}…
                          </span>
                          <Input
                            value={v.name}
                            onChange={(e) => handleUpdateVariant(v.id, { name: e.target.value })}
                            className="input-tactical h-7 text-xs flex-1"
                            placeholder="Nombre"
                            data-testid={`variant-name-${v.id}`}
                          />
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {COLORS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => handleUpdateVariant(v.id, { color: c })}
                              className={`w-5 h-5 rounded-sm transition-all ${
                                v.color === c ? 'ring-2 ring-white ring-offset-1 ring-offset-background' : ''
                              }`}
                              style={{ backgroundColor: c }}
                              data-testid={`variant-color-${v.id}-${c}`}
                              aria-label={`Color ${c}${v.color === c ? ' (seleccionado)' : ''}`}
                              aria-pressed={v.color === c}
                            />
                          ))}
                          <button
                            type="button"
                            onClick={() => handleUpdateVariant(v.id, { color: null })}
                            className="text-[10px] text-zinc-500 hover:text-zinc-300 px-1"
                            title="Quitar color (heredar del servicio)"
                            aria-label="Quitar color propio y heredar del servicio"
                            data-testid={`variant-color-clear-${v.id}`}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Switch
                          checked={v.active !== false}
                          onCheckedChange={(checked) => handleUpdateVariant(v.id, { active: checked })}
                          data-testid={`variant-active-${v.id}`}
                          aria-label="Variante activa"
                        />
                        <span className="text-[10px] text-zinc-500">
                          {v.active !== false ? 'activa' : 'inactiva'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.isPublic && (
              <div className="space-y-2 border border-zinc-800 rounded-sm p-3">
                <Label className="text-xs text-zinc-400 uppercase tracking-wide">Horario de disponibilidad pública (hora local)</Label>
                {DAY_KEYS.map(day => {
                  const ranges = rangesByDay[day] || [];
                  const active = ranges.length > 0;
                  const setDay = (newRanges) => setRangesByDay(r => ({ ...r, [day]: newRanges }));
                  return (
                    <div key={day} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{DAY_LABELS[day]}</span>
                        <Switch
                          checked={active}
                          onCheckedChange={(checked) => setDay(checked ? [{ from: '09:00', to: '13:00' }] : [])}
                        />
                      </div>
                      {active && (
                        <div className="pl-2 space-y-1">
                          {ranges.map((range, i) => {
                            const invalid = toMins(range.from) >= toMins(range.to);
                            return (
                              <div key={i} className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={range.from}
                                  maxLength={5}
                                  placeholder="09:00"
                                  className={`w-16 bg-zinc-800 border rounded px-2 py-1 text-sm text-white ${invalid ? 'border-red-500' : 'border-zinc-700'}`}
                                  onChange={(e) => {
                                    const newRanges = [...ranges];
                                    newRanges[i] = { ...range, from: e.target.value };
                                    setDay(newRanges);
                                  }}
                                />
                                <span className="text-zinc-500 text-xs">—</span>
                                <input
                                  type="text"
                                  value={range.to}
                                  maxLength={5}
                                  placeholder="13:00"
                                  className={`w-16 bg-zinc-800 border rounded px-2 py-1 text-sm text-white ${invalid ? 'border-red-500' : 'border-zinc-700'}`}
                                  onChange={(e) => {
                                    const newRanges = [...ranges];
                                    newRanges[i] = { ...range, to: e.target.value };
                                    setDay(newRanges);
                                  }}
                                />
                                {invalid && <span className="text-red-400 text-xs">desde &lt; hasta</span>}
                                <button
                                  type="button"
                                  onClick={() => setDay(ranges.filter((_, idx) => idx !== i))}
                                  className="text-zinc-500 hover:text-red-400 ml-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => setDay([...ranges, { from: '09:00', to: '13:00' }])}
                            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white mt-1"
                          >
                            <Plus className="w-3 h-3" /> Añadir rango
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleSave}
                disabled={formLoading}
                data-testid="save-service-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
